import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, MessageCircle, MapPin } from 'lucide-react';
import FAQAccordion from '../../components/FAQAccordion';
import { FAQSchema, BreadcrumbSchema } from '../../components/SchemaMarkup';
import { services, faqData, cities, businessData } from '../../data/seoData';

const MainzPage = () => {
  const city = cities.mainz;
  
  const pageFAQs = [
    faqData.find(f => f.id === 1),
    faqData.find(f => f.id === 3),
    { 
      id: 4, 
      question: 'Wie schnell bekomme ich einen Termin in Mainz?',
      answer: 'In der Regel sehr kurzfristig in Mainz. Am schnellsten geht es über WhatsApp oder Telefon.',
      category: 'Nach dem Unfall'
    },
    faqData.find(f => f.id === 5),
    faqData.find(f => f.id === 16),
    faqData.find(f => f.id === 20)
  ].filter(Boolean);

  const handleWhatsApp = () => window.open(`https://wa.me/${businessData.whatsapp}`, '_blank');
  const handleCall = () => window.location.href = `tel:${businessData.phone}`;

  const processSteps = [
    { number: 1, title: 'Kontakt aufnehmen', description: 'WhatsApp oder Telefon' },
    { number: 2, title: 'Vor-Ort Termin', description: 'Termin vereinbaren' },
    { number: 3, title: 'Dokumentation & Bewertung', description: 'Schadenaufnahme vor Ort' },
    { number: 4, title: 'Gutachten fertig & verwendbar', description: 'Rechtssicher nutzbar' }
  ];

  const commonTopics = [
    'Schadenhöhe & Reparaturkosten',
    'Wertminderung',
    'Nutzungsausfall',
    'Beweissicherung für die Regulierung'
  ];

  return (
    <div style={{ background: '#FFFFFF', position: 'relative' }}>
      <Helmet>
        <title>Kfz Gutachter Mainz | Unfallgutachten, Beweissicherung & Service</title>
        <meta name="description" content="Unabhängiger Kfz Gutachter in Mainz. Unfallgutachten, Beweissicherung, Wertminderung & Nutzungsausfall. Vor-Ort-Service + WhatsApp Kontakt." />
        <link rel="canonical" href={`${businessData.url}${city.url}`} />
      </Helmet>

      <FAQSchema faqs={pageFAQs} />
      <BreadcrumbSchema items={[{ name: 'Start', url: '/' }, { name: 'Standorte', url: '/' }, { name: 'Mainz', url: city.url }]} />

      <section style={{ paddingTop: '140px', paddingBottom: '80px', background: 'linear-gradient(180deg, #F0F4F8 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(8, 145, 178, 0.1)', border: '1px solid rgba(8, 145, 178, 0.3)', padding: '10px 20px', borderRadius: '50px', marginBottom: '24px' }}>
              <MapPin size={18} style={{ color: '#0891B2' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#0891B2', letterSpacing: '0.5px' }}>VOR ORT IN MAINZ</span>
            </div>

            <h1 style={{ fontSize: 'clamp(36px, 6vw, 56px)', fontWeight: '700', lineHeight: '1.2', color: '#1a202c', marginBottom: '24px' }}>
              Kfz Gutachter Mainz
            </h1>
            
            <p style={{ fontSize: '20px', lineHeight: '1.7', color: '#4a5568', marginBottom: '40px' }}>
              Unfall oder Schaden in Mainz? Wir kommen vor Ort, dokumentieren sauber und erstellen ein Gutachten, das du direkt nutzen kannst.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleWhatsApp} className="btn-primary" style={{ minHeight: '56px' }}>
                <MessageCircle size={20} />WhatsApp Soforthilfe
              </button>
              <button onClick={handleCall} className="btn-secondary" style={{ minHeight: '56px' }}>
                <Phone size={20} />Jetzt anrufen
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
            Vor Ort in Mainz & Umgebung
          </h2>
          <p style={{ fontSize: '18px', lineHeight: '1.8', color: '#4a5568', marginBottom: '32px' }}>
            Wir sind in Mainz und Umgebung im Einsatz und begutachten Fahrzeuge dort, wo es für dich am einfachsten ist.
          </p>
          <p style={{ fontSize: '18px', lineHeight: '1.8', color: '#4a5568' }}>
            Unser Ziel: schnelle Terminvergabe, klare Kommunikation und saubere Dokumentation.
          </p>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#F8F9FA' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '32px' }}>
            Häufige Themen nach einem Unfall
          </h2>
          <div className="glass-card" style={{ padding: '40px' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {commonTopics.map((topic, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', fontSize: '18px', color: '#4a5568' }}>
                  <CheckCircle size={20} style={{ color: '#0891B2', marginTop: '4px', flexShrink: 0 }} />
                  <span>{topic}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '16px' }}>
              Unsere Leistungen
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568' }}>
              Von Unfallgutachten bis Beweissicherung: Hier findest du alle Leistungen im Überblick.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(services).map((service, index) => (
              <Link key={index} to={service.url} className="glass-card" style={{ padding: '28px', textDecoration: 'none', display: 'block' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', marginBottom: '12px' }}>
                  {service.name}
                </h3>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#0891B2', fontSize: '15px', fontWeight: '600' }}>
                  Mehr erfahren<ArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#F8F9FA' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '60px', textAlign: 'center' }}>
            Ablauf
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <div key={step.number} style={{ textAlign: 'center' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15) 0%, rgba(6, 182, 212, 0.1) 100%)', border: '2px solid rgba(8, 145, 178, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: '800', color: '#0891B2', margin: '0 auto 20px' }}>
                  {step.number}
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1a202c', marginBottom: '12px' }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: '15px', color: '#4a5568', lineHeight: '1.6' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1000px' }}>
          <div className="glass-card" style={{ padding: '40px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '24px' }}>
              Einsatzgebiet
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Vor-Ort Begutachtung in Mainz flexibel möglich',
                'Schneller Erstkontakt per WhatsApp',
                'Rhein-Main Einsatzgebiet inklusive Umgebung'
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', fontSize: '16px', color: '#4a5568' }}>
                  <CheckCircle size={20} style={{ color: '#0891B2', marginTop: '2px', flexShrink: 0 }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#F8F9FA' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '16px' }}>
              Häufig gestellte Fragen
            </h2>
          </div>
          <FAQAccordion faqs={pageFAQs} />
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/faq" style={{ color: '#0891B2', fontSize: '16px', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Alle Fragen & Antworten<ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="pad-large" style={{ background: '#FFFFFF' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(6, 182, 212, 0.05) 100%)', border: '1px solid rgba(8, 145, 178, 0.2)', borderRadius: '24px', padding: '60px 40px', textAlign: 'center' }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: '700', color: '#1a202c', marginBottom: '20px' }}>
              Schnell Hilfe in Mainz
            </h2>
            <p style={{ fontSize: '18px', color: '#4a5568', lineHeight: '1.7', marginBottom: '40px', maxWidth: '600px', margin: '0 auto 40px' }}>
              Schick uns kurz die Infos und wir melden uns schnell zurück.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" style={{ marginBottom: '32px' }}>
              <button onClick={handleWhatsApp} className="btn-primary" style={{ minHeight: '56px' }}>
                <MessageCircle size={20} />WhatsApp
              </button>
              <button onClick={handleCall} className="btn-secondary" style={{ minHeight: '56px' }}>
                <Phone size={20} />Anrufen
              </button>
            </div>
            <Link to="/kontakt" style={{ color: '#0891B2', fontSize: '16px', fontWeight: '600', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Kontaktformular<ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainzPage;
